import { useState } from "react";
import { useNavigate, Navigate, Link } from "react-router-dom";
import {
  Lock,
  User,
  CheckCircle2,
  Eye,
  EyeOff,
  Printer,
  Sparkles,
  ArrowRight,
  Headphones,
  Phone,
  ShieldCheck,
} from "lucide-react";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import CustomerSupportModal from "@/components/CustomerSupportModal";

function ChangePasswordScreen({
  user,
  newPassword,
  confirmPassword,
  setNewPassword,
  setConfirmPassword,
  showNewPassword,
  setShowNewPassword,
  showConfirmPassword,
  setShowConfirmPassword,
  passwordError,
  passwordSuccess,
  busy,
  setBusy,
  setPasswordError,
  setPasswordSuccess,
  refreshUser,
}) {
  const { login } = useAuth();
  const nav = useNavigate();

  const submitChangePassword = async (e) => {
    e.preventDefault();
    setPasswordError("");
    setPasswordSuccess("");

    if (newPassword.length < 4) {
      setPasswordError("Password must be at least 4 characters.");
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordError("Passwords do not match.");
      return;
    }

    setBusy(true);
    try {
      const { api } = await import("@/lib/api");
      await api.post("/auth/change-password", {
        new_password: newPassword,
        confirm_password: confirmPassword,
      });

      setPasswordSuccess("Password updated successfully!");
      await refreshUser();
      setTimeout(() => {
        nav("/");
      }, 800);
    } catch (err) {
      setPasswordError(formatApiError(err) || "Failed to update password.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 bg-zinc-950 text-white relative overflow-hidden">
      <div className="absolute -top-40 -left-40 w-96 h-96 rounded-full bg-amber-500/10 blur-[100px] pointer-events-none" />
      <div className="absolute -bottom-40 -right-40 w-96 h-96 rounded-full bg-emerald-500/10 blur-[100px] pointer-events-none" />

      <div className="relative w-full max-w-md bg-zinc-900/90 border border-zinc-800 rounded-3xl p-8 shadow-2xl backdrop-blur-xl animate-fade-in">
        <div className="text-center mb-6">
          <div className="inline-block mb-3">
            <Logo size="lg" />
          </div>
          <h2 className="text-lg font-bold text-white">Set New Password</h2>
          <p className="text-xs text-zinc-400 mt-1">
            Please create a new password to secure your PrintQ shop account.
          </p>
        </div>

        <form onSubmit={submitChangePassword} className="space-y-4">
          <div>
            <label className="text-xs text-zinc-400 font-medium block mb-1.5">New Password</label>
            <div className="relative">
              <Lock className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-zinc-500" />
              <input
                type={showNewPassword ? "text" : "password"}
                required
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="w-full bg-zinc-950 border border-zinc-800 rounded-xl pl-10 pr-10 py-3 text-xs text-white placeholder-zinc-500 focus:outline-none focus:border-amber-500 transition-colors"
                placeholder="Enter new password"
              />
              <button
                type="button"
                onClick={() => setShowNewPassword(!showNewPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300"
              >
                {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          <div>
            <label className="text-xs text-zinc-400 font-medium block mb-1.5">Confirm Password</label>
            <div className="relative">
              <Lock className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-zinc-500" />
              <input
                type={showConfirmPassword ? "text" : "password"}
                required
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full bg-zinc-950 border border-zinc-800 rounded-xl pl-10 pr-10 py-3 text-xs text-white placeholder-zinc-500 focus:outline-none focus:border-amber-500 transition-colors"
                placeholder="Re-enter new password"
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300"
              >
                {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          {passwordError && (
            <div className="p-3 rounded-xl bg-red-500/10 border border-red-500/30 text-red-400 text-xs">
              {passwordError}
            </div>
          )}

          {passwordSuccess && (
            <div className="p-3 rounded-xl bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 text-xs flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4" />
              {passwordSuccess}
            </div>
          )}

          <button
            type="submit"
            disabled={busy}
            className="w-full py-3.5 rounded-xl bg-gradient-to-r from-amber-400 to-amber-500 hover:brightness-110 text-zinc-950 font-bold text-xs shadow-lg shadow-amber-500/20 transition-all active:scale-[0.98]"
          >
            {busy ? "Updating Password..." : "Save Password & Open PrintQ"}
          </button>
        </form>
      </div>
    </div>
  );
}

export default function Login() {
  const { user, login, error: loginError, refreshUser } = useAuth();
  const nav = useNavigate();

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [busy, setBusy] = useState(false);
  const [localError, setLocalError] = useState("");
  const [showSupportModal, setShowSupportModal] = useState(false);

  // Change password state
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordError, setPasswordError] = useState("");
  const [passwordSuccess, setPasswordSuccess] = useState("");

  // If already logged in and no compulsory password reset, redirect to Scan to Print studio
  if (user && !user.must_change_password) {
    return <Navigate to="/" replace />;
  }

  // First-time user password reset
  if (user?.must_change_password) {
    return (
      <ChangePasswordScreen
        user={user}
        newPassword={newPassword}
        confirmPassword={confirmPassword}
        setNewPassword={setNewPassword}
        setConfirmPassword={setConfirmPassword}
        showNewPassword={showNewPassword}
        setShowNewPassword={setShowNewPassword}
        showConfirmPassword={showConfirmPassword}
        setShowConfirmPassword={setShowConfirmPassword}
        passwordError={passwordError}
        passwordSuccess={passwordSuccess}
        busy={busy}
        setBusy={setBusy}
        setPasswordError={setPasswordError}
        setPasswordSuccess={setPasswordSuccess}
        refreshUser={refreshUser}
      />
    );
  }

  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    setLocalError("");
    setBusy(true);

    try {
      const loggedIn = await login(username.trim(), password);
      if (loggedIn) {
        if (!loggedIn.must_change_password) {
          nav("/");
        }
      } else {
        setLocalError(loginError || "Invalid username or password.");
      }
    } catch (err) {
      setLocalError("Unable to connect to PrintQ service. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 bg-zinc-950 text-white relative overflow-hidden select-none">
      {/* Dynamic ambient backdrop */}
      <div className="absolute -top-48 -left-48 w-[480px] h-[480px] rounded-full bg-amber-500/10 blur-[120px] pointer-events-none animate-pulse" />
      <div className="absolute -bottom-48 -right-48 w-[480px] h-[480px] rounded-full bg-emerald-500/10 blur-[120px] pointer-events-none animate-pulse" />

      <div className="relative w-full max-w-md flex flex-col gap-6">
        {/* Top Logo & Title */}
        <div className="text-center flex flex-col items-center">
          <Logo size="lg" />
        </div>

        {/* Single Login Form Card */}
        <form
          onSubmit={handleLoginSubmit}
          className="bg-zinc-900/90 border border-zinc-800/90 rounded-3xl p-8 shadow-2xl backdrop-blur-xl flex flex-col gap-5 transition-all duration-300"
          data-testid="printq-login-form"
        >
          <div>
            <h2 className="text-base font-bold text-white flex items-center gap-2">
              <Lock className="w-4 h-4 text-amber-400" />
              Shop Login
            </h2>
            <p className="text-xs text-zinc-400 mt-0.5">
              Enter your shop credentials to access PrintQ Studio.
            </p>
          </div>

          {/* Username Input */}
          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-zinc-300">Username / User ID</label>
            <div className="relative">
              <User className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-zinc-500" />
              <input
                type="text"
                required
                autoFocus
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="w-full bg-zinc-950 border border-zinc-800 rounded-xl pl-10 pr-4 py-3 text-xs text-white placeholder-zinc-500 focus:outline-none focus:border-amber-500 transition-colors shadow-inner"
                placeholder="Enter shop username"
                data-testid="login-username"
              />
            </div>
          </div>

          {/* Password Input */}
          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-zinc-300">Password</label>
            <div className="relative">
              <Lock className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-zinc-500" />
              <input
                type={showPassword ? "text" : "password"}
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full bg-zinc-950 border border-zinc-800 rounded-xl pl-10 pr-10 py-3 text-xs text-white placeholder-zinc-500 focus:outline-none focus:border-amber-500 transition-colors shadow-inner"
                placeholder="Enter password"
                data-testid="login-password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3.5 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300"
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          {/* Error Feedback */}
          {(localError || loginError) && (
            <div className="p-3 rounded-xl bg-red-500/10 border border-red-500/30 text-red-400 text-xs animate-shake">
              {localError || loginError}
            </div>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            disabled={busy}
            className="w-full py-3.5 px-6 rounded-xl bg-gradient-to-r from-amber-400 via-amber-500 to-emerald-500 hover:brightness-110 text-zinc-950 font-bold text-xs shadow-lg shadow-amber-500/25 flex items-center justify-center gap-2 transition-all duration-200 active:scale-[0.98] disabled:opacity-60"
            data-testid="login-submit-btn"
          >
            {busy ? (
              <span>Logging in...</span>
            ) : (
              <>
                <span>Open PrintQ Studio</span>
                <ArrowRight className="w-4 h-4" />
              </>
            )}
          </button>
        </form>

        {/* Customer Support Card */}
        <div className="bg-zinc-900/60 border border-zinc-800/80 rounded-2xl p-3.5 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-amber-500/10 border border-amber-500/20 flex items-center justify-center text-amber-400">
              <Headphones className="w-4 h-4" />
            </div>
            <div>
              <div className="text-xs font-bold text-white">Customer Support</div>
              <div className="text-[10px] text-zinc-400">Kunal Warkante (+91 9545262332)</div>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setShowSupportModal(true)}
            className="px-2.5 py-1 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-xs font-semibold text-amber-300 transition-colors"
          >
            Get Help
          </button>
        </div>

        {/* Footer Attribution & Discreet Master Admin Link */}
        <div className="flex items-center justify-between text-[10px] text-zinc-500 px-2">
          <span>PrintQ • JSM DIGITAL SOLUTIONS</span>
          <Link
            to="/master-admin/login"
            className="text-zinc-500 hover:text-amber-400 transition-colors font-medium flex items-center gap-1"
          >
            <ShieldCheck className="w-3 h-3" />
            <span>Master Console</span>
          </Link>
        </div>
      </div>

      <CustomerSupportModal
        isOpen={showSupportModal}
        onClose={() => setShowSupportModal(false)}
      />
    </div>
  );
}